import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AnimatedProgress } from '@/components/ui/animated-progress';
import { CircularProgress } from '@/components/ui/circular-progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Target, Plus, Trash2, CalendarIcon, CheckCircle, Clock, AlertTriangle, Edit, HelpCircle } from 'lucide-react';
import { format, parseISO, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, isWithinInterval, differenceInDays } from 'date-fns';
import { cn } from '@/lib/utils';
import AnimatedCounter from '@/components/ui/animated-counter';

interface Goal {
  id: string;
  name: string;
  target_amount: number;
  period_type: 'monthly' | 'quarterly' | 'yearly';
  start_date: string;
  end_date: string;
  is_active: boolean;
  created_at: string;
}

interface GoalWithProgress extends Goal {
  currentSavings: number;
  progressPercent: number;
  daysRemaining: number;
  status: 'on_track' | 'at_risk' | 'behind' | 'completed' | 'upcoming';
}

interface CostDefaults {
  HOME: number;
  PCP: number;
  URGENT_CARE: number;
  ER_NOW: number;
}

interface CostSavingsGoalsProps {
  agencyId: string | null;
  customHospitalCost?: number | null;
  userId?: string | null;
}

export default function CostSavingsGoals({ agencyId, customHospitalCost, userId }: CostSavingsGoalsProps) {
  const chartColors = useChartColors();
  const [goals, setGoals] = useState<GoalWithProgress[]>([]);
  const [costDefaults, setCostDefaults] = useState<CostDefaults>({
    HOME: 150,
    PCP: 250,
    URGENT_CARE: 500,
    ER_NOW: 2500
  });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formName, setFormName] = useState('');
  const [formTarget, setFormTarget] = useState('');
  const [formPeriodType, setFormPeriodType] = useState<'monthly' | 'quarterly' | 'yearly'>('monthly');
  const [formStartDate, setFormStartDate] = useState<Date | undefined>(new Date());
  const [formEndDate, setFormEndDate] = useState<Date | undefined>(undefined);

  useEffect(() => {
    loadCostDefaults();
  }, []);

  useEffect(() => {
    if (agencyId) {
      loadGoals();
    }
  }, [agencyId, costDefaults, customHospitalCost]);

  useEffect(() => {
    // Auto-calculate end date based on period type
    if (formStartDate && formPeriodType) {
      let endDate: Date;
      switch (formPeriodType) {
        case 'monthly':
          endDate = endOfMonth(formStartDate);
          break;
        case 'quarterly':
          endDate = endOfQuarter(formStartDate);
          break;
        case 'yearly':
          endDate = endOfYear(formStartDate);
          break;
      }
      setFormEndDate(endDate);
    }
  }, [formStartDate, formPeriodType]);

  const loadCostDefaults = async () => {
    try {
      const { data, error } = await supabase
        .from('hospital_visit_cost_defaults')
        .select('recommendation_level, default_cost');

      if (error) throw error;

      if (data) {
        const defaults: CostDefaults = { HOME: 150, PCP: 250, URGENT_CARE: 500, ER_NOW: 2500 };
        data.forEach((row: { recommendation_level: string; default_cost: number }) => {
          if (row.recommendation_level in defaults) {
            defaults[row.recommendation_level as keyof CostDefaults] = row.default_cost;
          }
        });
        setCostDefaults(defaults);
      }
    } catch (error) {
      console.error('Error loading cost defaults:', error);
    }
  };

  const calculateSavingsForPeriod = async (startDate: string, endDate: string): Promise<number> => {
    if (!agencyId) return 0;

    try {
      const { data: sessions, error } = await supabase
        .from('triage_sessions')
        .select('recommendation')
        .eq('agency_id', agencyId)
        .not('completed_at', 'is', null)
        .gte('completed_at', startDate)
        .lte('completed_at', endDate);

      if (error) throw error;

      const effectiveErCost = customHospitalCost || costDefaults.ER_NOW;
      let savings = 0;

      (sessions || []).forEach(session => {
        switch (session.recommendation) {
          case 'HOME':
            savings += effectiveErCost - costDefaults.HOME;
            break;
          case 'PCP':
            savings += effectiveErCost - costDefaults.PCP;
            break;
          case 'URGENT_CARE':
            savings += effectiveErCost - costDefaults.URGENT_CARE;
            break;
        }
      });

      return savings;
    } catch (error) {
      console.error('Error calculating savings:', error);
      return 0;
    }
  };

  const loadGoals = async () => {
    if (!agencyId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cost_savings_goals')
        .select('*')
        .eq('agency_id', agencyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const goalsWithProgress: GoalWithProgress[] = await Promise.all(
        (data || []).map(async (goalRow) => {
          const goal: Goal = {
            ...goalRow,
            period_type: goalRow.period_type as 'monthly' | 'quarterly' | 'yearly'
          };
          const currentSavings = await calculateSavingsForPeriod(goal.start_date, goal.end_date);
          const progressPercent = Math.min(100, (currentSavings / goal.target_amount) * 100);
          const now = new Date();
          const startDate = parseISO(goal.start_date);
          const endDate = parseISO(goal.end_date);
          const daysRemaining = Math.max(0, differenceInDays(endDate, now));
          
          let status: GoalWithProgress['status'];
          if (now < startDate) {
            status = 'upcoming';
          } else if (progressPercent >= 100) {
            status = 'completed';
          } else if (now > endDate) {
            status = 'behind';
          } else {
            // Calculate expected progress based on time elapsed
            const totalDays = differenceInDays(endDate, startDate);
            const elapsedDays = differenceInDays(now, startDate);
            const expectedProgress = (elapsedDays / totalDays) * 100;
            
            if (progressPercent >= expectedProgress - 10) {
              status = 'on_track';
            } else if (progressPercent >= expectedProgress - 25) {
              status = 'at_risk';
            } else {
              status = 'behind';
            }
          }

          return {
            ...goal,
            currentSavings,
            progressPercent,
            daysRemaining,
            status
          };
        })
      );

      setGoals(goalsWithProgress);
    } catch (error) {
      console.error('Error loading goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormName('');
    setFormTarget('');
    setFormPeriodType('monthly');
    setFormStartDate(new Date());
    setFormEndDate(undefined);
    setEditingGoal(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (goal: Goal) => {
    setEditingGoal(goal);
    setFormName(goal.name);
    setFormTarget(goal.target_amount.toString());
    setFormPeriodType(goal.period_type);
    setFormStartDate(parseISO(goal.start_date));
    setFormEndDate(parseISO(goal.end_date));
    setDialogOpen(true);
  };

  const handleSaveGoal = async () => {
    if (!agencyId || !formName || !formTarget || !formStartDate || !formEndDate) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      const goalData = {
        agency_id: agencyId,
        name: formName,
        target_amount: parseFloat(formTarget),
        period_type: formPeriodType,
        start_date: format(formStartDate, 'yyyy-MM-dd'),
        end_date: format(formEndDate, 'yyyy-MM-dd'),
        is_active: true,
        created_by: userId
      };

      if (editingGoal) {
        const { error } = await supabase
          .from('cost_savings_goals')
          .update(goalData)
          .eq('id', editingGoal.id);

        if (error) throw error;
        toast.success('Goal updated successfully');
      } else {
        const { error } = await supabase
          .from('cost_savings_goals')
          .insert(goalData);

        if (error) throw error;
        toast.success('Goal created successfully');
      }

      setDialogOpen(false);
      resetForm();
      await loadGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
      toast.error('Failed to save goal');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    try {
      const { error } = await supabase
        .from('cost_savings_goals')
        .delete()
        .eq('id', goalId);

      if (error) throw error;
      toast.success('Goal deleted');
      await loadGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getStatusBadge = (status: GoalWithProgress['status']) => {
    const config = {
      on_track: { label: 'On Track', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      at_risk: { label: 'At Risk', className: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      behind: { label: 'Behind', className: 'bg-red-100 text-red-800', icon: AlertTriangle },
      completed: { label: 'Completed', className: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      upcoming: { label: 'Upcoming', className: 'bg-gray-100 text-gray-800', icon: Clock }
    };
    const { label, className, icon: Icon } = config[status];
    return (
      <Badge variant="secondary" className={cn('gap-1', className)}>
        <Icon className="h-3 w-3" />
        {label}
      </Badge>
    );
  };

  const getPeriodLabel = (type: string) => {
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Savings Goals</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="h-24 bg-muted rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center space-x-2">
                <Target className="h-5 w-5 text-green-600" />
                <span>Savings Goals</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-4 w-4 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-xs">
                      <p className="text-sm">Each goal has its own date range. Not affected by the date filter above.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </CardTitle>
              <CardDescription>
                Set and track cost savings targets
              </CardDescription>
            </div>
            <Button onClick={openCreateDialog} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Goal
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {goals.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No savings goals set</p>
              <p className="text-sm">Create a goal to start tracking your progress</p>
            </div>
          ) : (
            <div className="space-y-4">
              {goals.map(goal => (
                <div key={goal.id} className="p-4 rounded-lg border border-border bg-muted/30">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-4">
                      {/* Circular progress gauge */}
                      <CircularProgress
                        value={goal.progressPercent}
                        size={64}
                        strokeWidth={6}
                        duration={1500}
                        showLabel={false}
                        indicatorClassName={cn(
                          goal.status === 'completed' && 'stroke-blue-500',
                          goal.status === 'on_track' && 'stroke-green-500',
                          goal.status === 'at_risk' && 'stroke-yellow-500',
                          goal.status === 'behind' && 'stroke-red-500',
                          goal.status === 'upcoming' && 'stroke-gray-400'
                        )}
                      >
                        <span className="text-sm font-bold tabular-nums">
                          <AnimatedCounter value={Math.round(goal.progressPercent)} duration={1500} />%
                        </span>
                      </CircularProgress>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-medium text-foreground">{goal.name}</h4>
                          {getStatusBadge(goal.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {getPeriodLabel(goal.period_type)} • {format(parseISO(goal.start_date), 'MMM d')} - {format(parseISO(goal.end_date), 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(goal)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteGoal(goal.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium tabular-nums">
                        <AnimatedCounter 
                          value={goal.currentSavings} 
                          duration={1200}
                          formatOptions={{
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                          }}
                        /> / {formatCurrency(goal.target_amount)}
                      </span>
                    </div>
                    <AnimatedProgress 
                      value={goal.progressPercent} 
                      duration={1200}
                      className="h-2"
                      indicatorClassName={cn(
                        goal.status === 'completed' && 'bg-blue-600',
                        goal.status === 'on_track' && 'bg-green-600',
                        goal.status === 'at_risk' && 'bg-yellow-500',
                        goal.status === 'behind' && 'bg-red-500',
                        goal.status === 'upcoming' && 'bg-gray-400'
                      )}
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span className="tabular-nums"><AnimatedCounter value={goal.progressPercent} duration={1200} decimals={1} />% complete</span>
                      {goal.status !== 'completed' && goal.status !== 'upcoming' && (
                        <span>{goal.daysRemaining} days remaining</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Goal Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingGoal ? 'Edit Goal' : 'Create Savings Goal'}</DialogTitle>
            <DialogDescription>
              Set a target for cost savings to track your progress
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="goal-name">Goal Name</Label>
              <Input
                id="goal-name"
                placeholder="e.g., Q1 Savings Target"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="goal-target">Target Amount ($)</Label>
              <Input
                id="goal-target"
                type="number"
                placeholder="e.g., 50000"
                value={formTarget}
                onChange={(e) => setFormTarget(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Period Type</Label>
              <Select value={formPeriodType} onValueChange={(v) => setFormPeriodType(v as any)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formStartDate ? format(formStartDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formStartDate}
                      onSelect={setFormStartDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formEndDate ? format(formEndDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formEndDate}
                      onSelect={setFormEndDate}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveGoal} disabled={saving}>
              {saving ? 'Saving...' : editingGoal ? 'Update Goal' : 'Create Goal'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
